import React from "react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";
import { TrustList } from "../components/TrustList";
import FaqAccordion from "../components/FaqAccordion";
import ReviewCard from "../components/ReviewCard";
import { Home, FileText, Wrench, Zap, CheckCircle2 } from "lucide-react";
import { reviews, pageHeroImages } from "../data/mockData";
import Seo from "../components/Seo";
import { PAGE_SEO, SITE_URL } from "../lib/seo";

const landlordSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Landlord Gas Safety Certificates (CP12) & Boiler Services",
  provider: { "@type": "LocalBusiness", name: "TOFT Gas, Heating & Plumbing", telephone: "+447885592820" },
  areaServed: ["Rhondda Cynon Taf", "Cardiff", "Bridgend", "Caerphilly"],
  url: `${SITE_URL}/landlord-services`,
  offers: { "@type": "Offer", priceCurrency: "GBP", price: "70", description: "CP12 Certificates from £70" },
};

const landlordFaqs = [
  {
    id: 1,
    question: "How much is a CP12 Certificate?",
    answer: "CP12 Certificates start from £70.",
  },
  {
    id: 2,
    question: "Do I need a CP12 every year?",
    answer:
      "Yes. Landlords are legally required to arrange gas safety inspections every 12 months.",
  },
  {
    id: 3,
    question: "Can I book a boiler service and CP12 together?",
    answer:
      "Yes. Many landlords book both together and discounts may be available.",
  },
  {
    id: 4,
    question: "Do you work with HMOs?",
    answer:
      "Yes. We regularly work with landlords, HMOs and larger property portfolios.",
  },
];

const LandlordServicesPage = () => {
  const steveReview = reviews.find((r) => r.name === "Steve");

  return (
    <>
      <Seo {...PAGE_SEO.landlord} jsonLd={landlordSchema} />
      <PageHero
        eyebrow="Landlord Services"
        image={pageHeroImages.landlord}
        title="Landlord Gas Safety Certificates (CP12) from £70 — Cardiff, RCT, Bridgend &amp; Caerphilly"
        subtitle="Fast CP12 certificates from £70 for landlords, HMO owners and property investors. Landlord gas safety certificates available across Cardiff, Rhondda Cynon Taf, Bridgend, Caerphilly and surrounding areas. As landlords ourselves, we understand compliance, communication and keeping costs sensible."
      />

      {/* CP12 Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-brand-blue text-white rounded-2xl p-8 lg:p-12 mb-12 shadow-xl">
            <div className="flex items-start gap-4 mb-5">
              <div className="h-14 w-14 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <FileText className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-brand-blue-light mb-1">
                  CP12 Certificates From £70
                </p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
                  Landlord Gas Safety Certificates (CP12)
                </h2>
              </div>
            </div>
            <div className="space-y-3 text-base sm:text-lg leading-relaxed text-blue-50">
              <p>
                If you rent out a property with gas appliances, a valid Landlord Gas
                Safety Certificate is a legal requirement.
              </p>
              <p>
                We provide annual gas safety inspections for landlords across South Wales,
                helping keep properties compliant and tenants safe.
              </p>
            </div>
            <div className="mt-6 bg-white/10 rounded-xl p-5">
              <p className="text-sm font-bold uppercase tracking-widest text-brand-blue-light mb-3">
                A CP12 inspection includes
              </p>
              <ul className="grid sm:grid-cols-2 gap-2">
                {["Gas appliances", "Pipework", "Flues", "Safety devices", "Ventilation where required"].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-base">
                    <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm italic">
                Certificates are issued promptly following inspection.
              </p>
            </div>
          </div>

          {/* What is CP12 */}
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
              What Is a CP12 Certificate?
            </h2>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-3">
              A CP12 Certificate, also known as a Landlord Gas Safety Certificate,
              confirms that gas appliances, flues and pipework within a rental property
              have been inspected by a Gas Safe registered engineer.
            </p>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              Landlords are legally required to arrange these checks every 12 months. If
              you're unsure when your certificate is due, simply get in touch and we'll be
              happy to advise.
            </p>
          </div>

          {/* Boiler Servicing for Landlords */}
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-4">
              Boiler Servicing for Landlords
            </h2>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
              Many landlords choose to combine their annual boiler service with their gas
              safety certificate.
            </p>
            <TrustList
              items={[
                "Reduce tenant callouts",
                "Improve boiler reliability",
                "Identify issues early",
                "Help maintain warranties",
                "Improve efficiency",
                "Keep tenants happy",
              ]}
              columns={2}
            />
            <p className="mt-5 text-base text-slate-700">
              <span className="font-semibold text-brand-navy">Boiler servicing starts from £70.</span>{" "}
              Discounts may be available when booked alongside a CP12 inspection.
            </p>
          </div>

          {/* Portfolio landlords */}
          <div className="bg-brand-slate-light rounded-2xl p-7 lg:p-10 mb-12">
            <div className="flex items-start gap-4 mb-4">
              <Home className="h-7 w-7 text-brand-blue shrink-0" />
              <h2 className="text-2xl sm:text-3xl font-black text-brand-navy">
                Portfolio Landlords & HMOs
              </h2>
            </div>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-4">
              Whether you own:
            </p>
            <ul className="space-y-2 mb-5">
              {["One rental property", "Multiple buy-to-lets", "HMOs", "Student accommodation", "A larger property portfolio"].map((i) => (
                <li key={i} className="flex items-start gap-2 text-base">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0 mt-0.5" />
                  <span>{i}</span>
                </li>
              ))}
            </ul>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
              Many of our landlord customers return year after year for annual gas safety
              inspections, boiler servicing and maintenance work.
            </p>
            <p className="mt-3 font-semibold text-brand-navy">
              Our aim is simple: become the contractor you can rely on whenever you need us.
            </p>
          </div>

          {/* Landlord Review */}
          {steveReview && (
            <div className="mb-12">
              <p className="text-xs font-bold tracking-widest uppercase text-brand-blue mb-4">
                Landlord Review
              </p>
              <ReviewCard review={steveReview} />
            </div>
          )}

          {/* Why landlords choose us */}
          <div className="mb-4">
            <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-6">
              Why Landlords Choose TOFT
            </h2>
            <TrustList
              items={[
                "Gas Safe Registered",
                "Fast CP12 Turnaround",
                "Boiler Servicing From £70",
                "CP12 Certificates From £70",
                "Honest Advice",
                "Reliable Appointments",
                "Landlord Experience",
                "Trusted By Property Owners Across South Wales",
              ]}
              columns={2}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-slate-light py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-black text-brand-navy mb-7">
            Frequently Asked Questions
          </h2>
          <FaqAccordion items={landlordFaqs} testId="landlord-faq" />
        </div>
      </section>

      <FinalCTA
        title="Need a CP12 or Boiler Service?"
        lines={[
          "Need a CP12 Certificate?",
          "Need a boiler service for your rental property?",
          "Need a reliable Gas Safe engineer you can trust year after year?",
        ]}
      />
    </>
  );
};

export default LandlordServicesPage;
